/**
 * AnalyserPanel -- admin-only BYO-LLM dispatch surface (P11.2).
 *
 * Lists existing FrameworkAnalyserRun rows for a cycle, lets admin
 * pick a control + paste an evidence excerpt + dispatch a fresh
 * run. Successful runs surface their suggested judgement inline
 * with an "Accept" button that UPSERTs the assessment using the
 * existing hook (LLM stays read-only at the data layer per the
 * P10 lock).
 *
 * Route: /framework/assessments/:code/cycles/:cycleId/analyser
 */
import { formatDateTime } from "shared/time/formatDate";
import { stateLabel } from "shared/vocab/workflowState";
import { i18n } from "shared/i18n";
import { useTranslation } from "react-i18next";

import { Figure } from "shared/components/Figure";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { EngineOffBanner } from "shared/components/EngineOffBanner";

import { api } from "shared/api/client";
import type { ApiError } from "shared/api/client";
import { hasPosition, useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";
import {
  clearAcceptance,
  recordAcceptance,
} from "framework/assessments/engineAcceptance";
import type { AnalyserRun, AssessmentStatus, FrameworkControl } from "shared/api/types";
import { EngineLanguageNote } from "shared/components/EngineLanguageNote";
import {
  useCancelAnalyserRun,
  useAnalyseBatch,
  useAnalyseControl,
  useAnalyserRuns,
  useCatalogue,
  useCycles,
  useRetryAnalyserRun,
  useCycleAssessments,
  useUpsertAssessment,
} from "framework/api/assessment_hooks";

// P31.3 (#553): Marsad Engine status payload from
// GET /v1/admin/marsad-engine/status. Inlined here because the
// AnalyserPanel is the only surface that consumes it today; lift
// to shared/api/types.ts when a second Engine surface needs it.
interface EngineStatus {
  configured: boolean;
  provider: "none" | "openai_compatible" | "anthropic_native" | "bedrock";
  base_url: string | null;
  model: string | null;
  bedrock_region: string | null;
  api_key_present: boolean;
  timeout_s: number;
}

interface EngineTestResult {
  ok: boolean;
  latency_ms: number;
  sample_output: string | null;
  error: string | null;
}

type DispatchMode = "single_control" | "batch_cycle";

const RUN_TONE: Record<string, string> = {
  queued: "bg-slate-100 text-slate-700 border-slate-300",
  running: "bg-amber-50 text-amber-900 border-amber-300",
  succeeded: "bg-emerald-50 text-emerald-900 border-emerald-300",
  failed: "bg-rose-50 text-rose-900 border-rose-300",
};

export function AnalyserPanel() {
  const { t } = useTranslation();
  const { frameworkCode = "", cycleId = "" } = useParams<{
    frameworkCode: string;
    cycleId: string;
  }>();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  // P263: the analyser is the preparer's grading aid, not an admin-only
  // tool. Admin still allowed (and admin satisfies the backend gate).
  const canRunEngine = isAdmin || hasPosition(me.data, "preparer");

  const cyclesQuery = useCycles(frameworkCode);
  const catalogueQuery = useCatalogue(frameworkCode);
  const runsQuery = useAnalyserRuns(cycleId);
  const analyse = useAnalyseControl(cycleId);
  const analyseBatch = useAnalyseBatch(cycleId);
  const [mode, setMode] = useState<DispatchMode>("single_control");
  const [maxControls, setMaxControls] = useState<number>(20);

  const cycle = useMemo(
    () => cyclesQuery.data?.find((c) => c.id === cycleId),
    [cyclesQuery.data, cycleId],
  );
  const controls = useMemo(() => {
    const list: FrameworkControl[] = [];
    for (const dom of catalogueQuery.data?.domains ?? []) {
      for (const sub of dom.subdomains) {
        for (const ctrl of sub.controls) list.push(ctrl);
      }
    }
    return list;
  }, [catalogueQuery.data]);

  const [filter, setFilter] = useState("");
  const filteredControls = useMemo(() => {
    if (!filter.trim()) return controls.slice(0, 20);
    const f = filter.trim().toLowerCase();
    return controls
      .filter(
        (c) =>
          c.control_natural_id.toLowerCase().includes(f) ||
          c.control_text.toLowerCase().includes(f),
      )
      .slice(0, 20);
  }, [controls, filter]);

  const [pickedControlId, setPickedControlId] = useState<string | null>(null);
  const [evidenceText, setEvidenceText] = useState("");
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  // P35.2 (#556): toast on terminal transitions observed via the
  // polling refetch. Tracks which run-ids have already triggered a
  // toast so flipping back through the panel doesn't re-fire.
  const toast = useToast();
  const toastedRunIdsRef = useRef<Set<string>>(new Set());
  // Round 15 A11 -- the runs THIS page dispatched. Dispatch is held only
  // while one of these is open: on the hosted deployment one queued run
  // that a restart had orphaned locked Dispatch for every seat on the
  // cycle for eighteen hours, because any open run counted as "mine".
  const ownRunIdsRef = useRef<Set<string>>(new Set());
  /* Round 14 G -- a toast is for a run that FINISHED while you watched.
     Every run already finished when the page first loaded was being
     "observed" finishing on arrival, so a preparer opening the page
     was told three runs she did not dispatch had just succeeded. The
     first load seeds the set silently; only a later transition toasts. */
  const seededRef = useRef(false);
  useEffect(() => {
    const runs = runsQuery.data;
    if (!runs) return;
    if (!seededRef.current) {
      seededRef.current = true;
      for (const run of runs) {
        if (run.status === "succeeded" || run.status === "failed") {
          toastedRunIdsRef.current.add(run.id);
        }
      }
      return;
    }
    for (const run of runs) {
      if (run.status !== "succeeded" && run.status !== "failed") continue;
      if (toastedRunIdsRef.current.has(run.id)) continue;
      toastedRunIdsRef.current.add(run.id);
      /* Round 16 A21 -- a run finishing is news to the person who
         dispatched it. opsres_demo was toasted another seat's failure
         because every open panel on the cycle watched every run. */
      if (!run.dispatched_by_me && !ownRunIdsRef.current.has(run.id)) continue;
      if (run.status === "succeeded") {
        toast.push({
          tone: "success",
          message: run.auto_dispatched
            ? t("screens.analyserPanel.toastAutoSucceeded")
            : t("screens.analyserPanel.toastSucceeded"),
          detail:
            run.mode === "batch_cycle"
              ? t("screens.analyserPanel.toastBatchGraded", {
                  count: run.controls_graded ?? 0,
                })
              : t("screens.analyserPanel.toastOpenRun"),
        });
      } else {
        toast.push({
          tone: "error",
          message: run.auto_dispatched
            ? t("screens.analyserPanel.toastAutoFailed")
            : t("screens.analyserPanel.toastFailed"),
          ...(run.error_detail ? { detail: run.error_detail } : {}),
        });
      }
    }
  }, [runsQuery.data, toast, t]);

  if (!canRunEngine) {
    return (
      <div
        data-testid="analyser-panel-not-admin"
        className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
      >
        {t("screens.analyserPanel.runningTheMarsadEngineNeedsThePreparer")}
      </div>
    );
  }

  const handleDispatch = async () => {
    if (!evidenceText.trim()) return;
    if (mode === "single_control") {
      if (!pickedControlId) return;
      const queued = await analyse.mutateAsync({
        control_id: pickedControlId,
        evidence_text: evidenceText.trim(),
      });
      // Round 16 A20 -- the bound is stated when it starts to run.
      toast.push({
        tone: "info",
        message: t("screens.analyserPanel.toastQueued"),
        detail: t("screens.analyserPanel.toastQueuedDetail"),
      });
      // Pre-mark the queued run so the effect above doesn't re-toast
      // it on its first poll.
      if (queued?.id) {
        toastedRunIdsRef.current.add(queued.id);
        ownRunIdsRef.current.add(queued.id);
      }
    } else {
      const queued = await analyseBatch.mutateAsync({
        evidence_text: evidenceText.trim(),
        max_controls: maxControls,
      });
      toast.push({
        tone: "info",
        message: t("screens.analyserPanel.toastBatchQueued"),
        detail: t("screens.analyserPanel.toastBatchQueuedDetail", {
          count: maxControls,
        }),
      });
      if (queued?.id) {
        toastedRunIdsRef.current.add(queued.id);
        ownRunIdsRef.current.add(queued.id);
      }
    }
    setEvidenceText("");
  };

  const dispatchPending = analyse.isPending || analyseBatch.isPending;
  // P35.2: "Queued" while the manual endpoint is round-tripping OR
  // while a run THIS page dispatched is open (round 15 A11: another
  // person's queued run, or one a restart orphaned, does not lock the
  // control; the row names it and offers cancel instead).
  const hasOpenRun =
    runsQuery.data?.some(
      (r) =>
        (r.status === "queued" || r.status === "running") &&
        ownRunIdsRef.current.has(r.id),
    ) ?? false;

  return (
    <div
      data-testid="analyser-panel-view"
      data-framework-code={frameworkCode}
      data-cycle-id={cycleId}
      className="flex flex-col gap-4"
    >
      <header className="space-y-2">
        <Link
          to={`/framework/assessments/${encodeURIComponent(frameworkCode)}/cycles/${encodeURIComponent(cycleId)}`}
          data-testid="analyser-back-link"
          className="text-xs text-brand-brassDark hover:text-brand-brass"
        >
          {t("screens.analyserPanel.backToAssessmentGrid")}
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("screens.analyserPanel.marsadEngine")}{" "}
          {cycle?.name ?? t("screens.analyserPanel.cycle")}
        </h1>
        <p className="text-xs text-slate-600">
          {t("screens.analyserPanel.singleControlAnalyserPickAControlPaste")}
        </p>
        {/* Round 14 G -- the badge reads an ADMIN endpoint, and it was
            mounted for every seat: a preparer's every visit put a 403 in
            the console. Everyone else has the availability banner just
            below, which reads the endpoint they may call. */}
        {isAdmin && <EngineStatusBadge />}
      </header>

      <EngineOffBanner manualKey="engineOff.analyser" />

      {/* Dispatch form */}
      <fieldset className="space-y-2 rounded border border-slate-200 bg-white p-3">
        <legend className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-1">
          {t("screens.analyserPanel.newAnalyserRun")}
        </legend>
        {/* Mode toggle */}
        <div data-testid="analyser-mode-toggle" className="flex gap-2 text-[11px]">
          {(["single_control", "batch_cycle"] as DispatchMode[]).map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`analyser-mode-${m}`}
              data-active={mode === m}
              onClick={() => setMode(m)}
              className={
                "rounded border px-2 py-0.5 font-semibold " +
                (mode === m
                  ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                  : "border-slate-300 bg-white text-slate-700")
              }
            >
              {m === "single_control"
                ? t("screens.analyserPanel.singleControl2")
                : t("screens.analyserPanel.batchCycleWalk")}
            </button>
          ))}
        </div>
        {mode === "single_control" && (
          <>
            <label className="block text-xs">
              <span className="text-slate-600">
                {t("screens.analyserPanel.searchControls")}
              </span>
              <input
                dir="auto"
                data-testid="analyser-control-filter"
                type="text"
                placeholder="e.g. 3.1.1-CR1 or MFA"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </label>
            <div
              data-testid="analyser-control-list"
              className="max-h-40 overflow-y-auto rounded border border-slate-200"
            >
              {filteredControls.map((c) => (
                <label
                  key={c.id}
                  data-testid="analyser-control-option"
                  data-control-id={c.id}
                  className={
                    "flex items-start gap-2 border-b border-slate-100 px-2 py-1 text-[11px] cursor-pointer " +
                    (pickedControlId === c.id ? "bg-emerald-50" : "")
                  }
                >
                  <input
                    type="radio"
                    name="control-pick"
                    value={c.id}
                    checked={pickedControlId === c.id}
                    onChange={() => setPickedControlId(c.id)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-mono text-[10px] text-slate-500">
                      {c.control_natural_id}
                    </span>
                    <span className="block text-slate-800">
                      {c.control_text.slice(0, 120)}
                      {c.control_text.length > 120 ? "…" : ""}
                    </span>
                  </span>
                </label>
              ))}
              {filteredControls.length === 0 && (
                <p className="px-2 py-2 text-[11px] italic text-slate-500">
                  {t("screens.analyserPanel.noControlsMatchTheFilter")}
                </p>
              )}
            </div>
          </>
        )}
        {mode === "batch_cycle" && (
          <label className="block text-xs">
            <span className="text-slate-600">
              {t("screens.analyserPanel.maxControlsToGrade150")}
            </span>
            <input
              data-testid="analyser-batch-max-controls"
              type="number"
              min={1}
              max={50}
              value={maxControls}
              onChange={(e) => setMaxControls(Number(e.target.value))}
              className="mt-1 block w-20 rounded border border-slate-300 px-2 py-1 text-xs"
            />
            <p className="mt-1 text-[10px] italic text-slate-500">
              {t("screens.analyserPanel.workerGradesControlsInDisplayOrderApproved")}
            </p>
          </label>
        )}
        <label className="block text-xs">
          <span className="text-slate-600">
            {t("screens.analyserPanel.evidenceExcerptPastePolicyParagraphControlRe")}
          </span>
          <textarea
            dir="auto"
            data-testid="analyser-evidence-text"
            value={evidenceText}
            onChange={(e) => setEvidenceText(e.target.value)}
            rows={5}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
        </label>
        {(analyse.error || analyseBatch.error) && (
          <p data-testid="analyser-dispatch-error" className="text-xs text-rose-700">
            {t("screens.analyserPanel.dispatchFailed")}{" "}
            {(analyse.error ?? analyseBatch.error) instanceof Error
              ? (analyse.error ?? analyseBatch.error)!.message
              : t("screens.analyserPanel.unknown")}
          </p>
        )}
        <button
          type="button"
          data-testid="analyser-dispatch-button"
          data-queued={hasOpenRun || dispatchPending}
          onClick={() => void handleDispatch()}
          disabled={
            !evidenceText.trim() ||
            dispatchPending ||
            hasOpenRun ||
            (mode === "single_control" && !pickedControlId)
          }
          className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
        >
          {dispatchPending
            ? t("screens.analyserPanel.dispatching")
            : hasOpenRun
              ? t("screens.analyserPanel.queued")
              : mode === "single_control"
                ? t("screens.analyserPanel.dispatchAnalyser")
                : t("screens.analyserPanel.dispatchBatchRun")}
        </button>
        {/* Round 17 A32 -- a grey button says why it is grey. */}
        {!dispatchPending &&
          !hasOpenRun &&
          (() => {
            const needsControl = mode === "single_control" && !pickedControlId;
            const needsExcerpt = !evidenceText.trim();
            if (!needsControl && !needsExcerpt) return null;
            const key =
              needsControl && needsExcerpt
                ? "dispatchNeedsBoth"
                : needsControl
                  ? "dispatchNeedsControl"
                  : "dispatchNeedsExcerpt";
            return (
              <p data-testid="analyser-dispatch-why" className="text-xs text-slate-500">
                {t(`screens.analyserPanel.${key}`)}
              </p>
            );
          })()}
      </fieldset>

      {/* Run history */}
      <section
        data-testid="analyser-runs-section"
        className="space-y-2 rounded border border-slate-200 bg-white p-3"
      >
        <h2 className="text-sm font-semibold text-slate-900">
          {t("screens.analyserPanel.analyserRuns")}
        </h2>
        {runsQuery.isLoading && (
          <p className="text-xs text-slate-500">
            {t("screens.analyserPanel.loadingRuns")}
          </p>
        )}
        {runsQuery.data && runsQuery.data.length === 0 && (
          <p
            data-testid="analyser-runs-empty"
            className="text-xs italic text-slate-500"
          >
            {t("screens.analyserPanel.noAnalyserRunsYetForThisCycle")}
          </p>
        )}
        {runsQuery.data && runsQuery.data.length > 0 && (
          <ul className="space-y-1">
            {runsQuery.data.map((run) => (
              <RunRow
                key={run.id}
                run={run}
                cycleId={cycleId}
                expanded={expandedRunId === run.id}
                onToggle={() =>
                  setExpandedRunId(expandedRunId === run.id ? null : run.id)
                }
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

interface RunRowProps {
  run: AnalyserRun;
  cycleId: string;
  expanded: boolean;
  onToggle: () => void;
}

/** ROUND 13 F6 -- what the judgement rests on, wherever it is read.
 *
 * Round 12 D2 put the excerpt and the capped-maturity reason inside
 * the accept confirmation, which is the right place for them and not
 * the only one: the result panel is where an analyst reads the verdict
 * and screenshots it, and there it said "partial - ML 3" and nothing
 * else. One component so the two cannot drift into saying different
 * things about one judgement.
 */
function JudgementProvenance({
  excerpt,
  anchored,
  fromDocuments = false,
  cappedReason,
  where,
}: {
  excerpt: string;
  anchored: boolean;
  /** Round 16 A26 -- a design test reads the covering documents. */
  fromDocuments?: boolean;
  cappedReason: string;
  where: "result" | "confirm";
}) {
  const { t } = useTranslation();
  if (!excerpt && !cappedReason) return null;
  return (
    <div className="space-y-1" data-testid={`analyser-provenance-${where}`}>
      {excerpt && (
        <div data-testid={`analyser-evidence-excerpt-${where}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t(
              fromDocuments
                ? "screens.analyserPanel.judgedFromDocuments"
                : anchored
                  ? "screens.analyserPanel.judgedFromArtefact"
                  : "screens.analyserPanel.judgedFromTyped",
            )}
          </p>
          <blockquote className="mt-0.5 border-s-2 border-slate-300 ps-2 text-[11px] italic text-slate-700">
            {excerpt}
          </blockquote>
        </div>
      )}
      {cappedReason && (
        <p
          data-testid={`analyser-capped-reason-${where}`}
          className="text-[11px] font-medium text-amber-900"
        >
          {cappedReason}
        </p>
      )}
    </div>
  );
}

function RunRow({ run, cycleId, expanded, onToggle }: RunRowProps) {
  const { t } = useTranslation();
  const tone = RUN_TONE[run.status] ?? RUN_TONE.queued;
  const upsert = useUpsertAssessment(cycleId);
  const toast = useToast();
  const me = useMe();
  // D1 -- what Accept would REPLACE. Fetched only while the row is
  // expanded: a list of runs must not pull the whole cycle's
  // assessments to render its collapsed rows.
  const assessments = useCycleAssessments(expanded ? cycleId : undefined);
  const out = (run.output_payload ?? {}) as Record<string, unknown>;
  const controlId = (out["control_id"] as string | undefined) ?? run.control_id ?? null;
  // Round 15 A12 -- the row itself names the control now (Alembic 0201),
  // so a queued run is not "(unknown control)" until its payload lands.
  const naturalId =
    (out["control_natural_id"] as string | undefined) ?? run.control_natural_id ?? "";
  // Round 15 A11 -- a queued row says who queued it and offers cancel.
  const cancel = useCancelAnalyserRun(cycleId);
  const suggestedStatus =
    (out["suggested_status"] as AssessmentStatus | undefined) ?? null;
  const suggestedMl = (out["suggested_ml"] as number | undefined) ?? null;
  const suggestedSummary = (
    (out["suggested_summary"] as string | undefined) ?? ""
  ).trim();
  const suggestedGaps = ((out["suggested_gaps"] as string | undefined) ?? "").trim();
  const suggestedRecs = (
    (out["suggested_recommendations"] as string | undefined) ?? ""
  ).trim();
  /* Round 12 D2 -- what the judgement was made FROM. This is the one
     Engine surface whose output is recorded word for word on a bank's
     assessment row, and it was the only one with no guard: nothing
     reprinted the excerpt, so one unverified typed sentence and a whole
     policy document produced the same-looking panel. */
  const evidenceExcerpt = (out["evidence_excerpt"] as string | undefined) ?? "";
  // A design test reads the covering documents, never typed text (A26).
  const evidenceAnchored = out["evidence_anchored"] === true;
  const cappedReason = (out["capped_reason"] as string | undefined) ?? "";
  // Round 16 A26 -- a design test lands in this list too. Its proposal
  // waits on the control's drawer (Round 14 A1: a person accepts it
  // there), so Accept here wrote nothing and said nothing. Name it, and
  // send the reader to where the decision is taken.
  const isDesignTest = out["run_kind"] === "test_of_design";
  const drawerHref = controlId
    ? `/framework/assessments/${encodeURIComponent(run.framework_code)}/cycles/${encodeURIComponent(cycleId)}?control=${encodeURIComponent(controlId)}`
    : null;
  // And "0 in / 0 out" is a figure about nothing: an Engine that does
  // not report its token counts is not an Engine that used none.
  const hasTokens = (run.tokens_in ?? 0) > 0 || (run.tokens_out ?? 0) > 0;

  // D1 -- Accept overwrote the judgement, summary and gaps the
  // preparer had written five minutes earlier, in silence: no
  // confirmation, no toast, no way back. An Engine suggestion is a
  // proposal, and replacing somebody's reasoning with it is a decision
  // they have to be able to see and undo.
  const current = assessments.data?.find((a) => a.framework_control_id === controlId);
  const [confirming, setConfirming] = useState(false);

  const applyValues = async (body: {
    status: AssessmentStatus;
    current_ml: number | null;
    evidence_summary: string | null;
    gaps: string | null;
    recommendations: string | null;
    /** C3 -- which Engine run set this, or a flag to take its name off. */
    derived_from_run_id?: string;
    clear_derived_from_run?: boolean;
  }) => {
    if (!controlId) return;
    await upsert.mutateAsync({ controlId, body });
  };

  const handleAccept = async () => {
    if (!controlId || !suggestedStatus) return;
    // Captured BEFORE the write, so Undo restores what was there
    // rather than what the screen happens to show afterwards.
    const previous = current
      ? {
          status: current.status as AssessmentStatus,
          current_ml: current.current_ml,
          evidence_summary: current.evidence_summary,
          gaps: current.gaps,
          recommendations: current.recommendations,
        }
      : null;
    await applyValues({
      status: suggestedStatus,
      current_ml: suggestedMl,
      evidence_summary: suggestedSummary || null,
      gaps: suggestedGaps || null,
      recommendations: suggestedRecs || null,
      // C3 -- record that the Engine proposed this. The column and its
      // badge have existed since P13 and accepting never wrote them, so
      // the record kept no trace of the Engine's hand in a judgement a
      // person then confirmed. For a product whose claim is AI an
      // examiner can trust, "who decided this" is the whole question.
      derived_from_run_id: run.id,
    });
    setConfirming(false);
    // Round 10 C5 -- the acceptance outlives its toast now. Recorded
    // AFTER the write, so the save's own clear runs first and this is
    // what survives it.
    recordAcceptance(controlId, {
      runId: run.id,
      acceptedBy: me.data?.display_name ?? me.data?.username ?? "",
      acceptedAt: new Date().toISOString(),
      previous,
    });
    toast.push({
      tone: "success",
      message: t("screens.analyserPanel.acceptedToast", {
        name: me.data?.display_name ?? me.data?.username ?? "",
      }),
      // Undo is offered only where there IS a previous judgement to
      // restore. On a control graded for the first time there is
      // nothing to go back to, and an Undo that clears the row would
      // be a different act wearing the same word.
      ...(previous
        ? {
            action: {
              label: t("screens.analyserPanel.undo"),
              onClick: () => {
                // C3 -- restoring a human judgement must also take the
                // Engine's name off it, or the record attributes to the
                // Engine a grade the Engine did not set. And Undo used
                // to restore in silence: the reader could not tell it
                // from a click that did nothing.
                void applyValues({
                  ...previous,
                  clear_derived_from_run: true,
                }).then(() => {
                  clearAcceptance(controlId);
                  toast.push({
                    tone: "success",
                    message: t("screens.analyserPanel.undoneToast"),
                  });
                });
              },
            },
          }
        : {}),
    });
  };

  return (
    <li
      data-testid="analyser-run-row"
      data-run-id={run.id}
      data-status={run.status}
      data-mode={run.mode}
      className="rounded border border-slate-200 bg-white p-2 text-xs"
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 text-start"
      >
        <span className="flex items-baseline gap-2">
          <span
            className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}
          >
            {run.status}
          </span>
          <span
            data-testid="analyser-run-mode-badge"
            className="inline-block rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-violet-900"
          >
            {run.mode === "batch_cycle"
              ? t("screens.analyserPanel.batch")
              : t("screens.analyserPanel.single")}
          </span>
          {run.mode === "batch_cycle" && run.controls_total !== null && (
            <span
              data-testid="analyser-run-progress"
              className="font-mono text-[10px] text-slate-700"
            >
              {/* C1 -- one figure. Two numbers with a spaced separator
                  between them reorder in an RTL paragraph; the
                  font-mono rule already sets direction on this span, but
                  stating it as one figure is what makes it true of the
                  TEXT rather than of a class somebody may change. */}
              <Figure>
                {[run.controls_graded ?? 0, run.controls_total].join(" / ")}
              </Figure>
            </span>
          )}
          {run.mode === "single_control" && (
            <span className="font-mono text-[10px] text-slate-500">
              {naturalId || t("screens.analyserPanel.unknownControl")}
            </span>
          )}
          {isDesignTest && (
            <span
              data-testid="analyser-run-design-test"
              className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
            >
              {t("screens.analyserPanel.designTestRun")}
            </span>
          )}
          {/* Round 19 A45 -- on a shared seat the name is the same for
              her runs and a neighbour's; this press is what tells them
              apart. */}
          {run.dispatched_by_me && (
            <span
              data-testid="analyser-run-yours"
              className="rounded bg-brand-brass/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-brassDark"
            >
              {t("screens.analyserPanel.yours")}
            </span>
          )}
          {run.triggered_by_name && !run.dispatched_by_me && (
            <span
              data-testid="analyser-run-queued-by"
              className="text-[10px] text-slate-500"
            >
              {t("screens.analyserPanel.queuedBy", { name: run.triggered_by_name })}
            </span>
          )}
          {run.status === "queued" && (
            <span
              role="button"
              tabIndex={0}
              data-testid="analyser-run-cancel"
              onClick={(e) => {
                e.stopPropagation();
                void cancel.mutateAsync(run.id).then(
                  () =>
                    toast.push({
                      tone: "info",
                      message: t("screens.analyserPanel.cancelledToast"),
                    }),
                  (err: unknown) =>
                    toast.push({
                      tone: "error",
                      message: t("screens.analyserPanel.cancelFailedToast"),
                      detail: err instanceof Error ? err.message : String(err),
                    }),
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  void cancel.mutateAsync(run.id);
                }
              }}
              className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
            >
              {t("screens.analyserPanel.cancelRun")}
            </span>
          )}
          {hasTokens && (
            <span className="text-[10px] text-slate-500">
              {/* C1 -- "372 in / 159 out" reversed for the same reason:
                  two numbers with neutrals between them, in a span with
                  no direction of its own. */}
              <Figure>
                {[
                  run.tokens_in,
                  t("screens.analyserPanel.in"),
                  run.tokens_out ?? 0,
                  t("screens.analyserPanel.outTokens"),
                ].join(" ")}
              </Figure>
            </span>
          )}
        </span>
        <span className="font-mono text-[9px] text-slate-400">
          {formatDateTime(run.created_at, i18n.language)}
        </span>
      </button>
      {/* Round 17 A32 -- a failed run says why on its row; the reason
          was there only once the row was opened. */}
      {!expanded && run.status === "failed" && (
        <p data-testid="analyser-run-failed-reason" className="mt-1 text-rose-700">
          {run.error_detail ?? t("screens.analyserPanel.analyserFailed")}
        </p>
      )}
      {expanded && (
        <div
          data-testid="analyser-run-expanded"
          className="mt-2 space-y-2 rounded bg-slate-50 p-2"
        >
          {run.status === "failed" && (
            <div className="space-y-1">
              <p className="text-rose-700">
                {run.error_detail ?? t("screens.analyserPanel.analyserFailed")}
              </p>
              {/* Round 16 A21 -- the Engine had no answer: dispatching
                  the same excerpt again asks the same question. */}
              {run.failure_cause !== "engine_no_answer" && (
                <RetryButton run={run} cycleId={cycleId} />
              )}
            </div>
          )}
          {run.status === "succeeded" && run.mode === "single_control" && (
            <>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">
                  {t("screens.analyserPanel.suggestedStatus")}
                </span>
                <p
                  data-testid="analyser-suggested-status"
                  className="font-semibold text-slate-900"
                >
                  {suggestedStatus} {t("screens.analyserPanel.ml")} {suggestedMl}
                </p>
              </div>
              {/* ROUND 13 F6 -- where an analyst READS, not one click
                  further in.
                  The result panel said "partial - ML 3" and showed
                  neither the excerpt the judgement was made from nor
                  the fact that the Engine proposed compliant at
                  maturity 4 and the product held it down. Everything
                  good about this surface, and it is the best Engine
                  work in the product, was inside the accept
                  confirmation -- past the point where an analyst reads
                  and screenshots. */}
              <JudgementProvenance
                excerpt={evidenceExcerpt}
                anchored={evidenceAnchored}
                fromDocuments={isDesignTest}
                cappedReason={cappedReason}
                where="result"
              />
              {suggestedSummary && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {t("screens.analyserPanel.summary")}
                  </span>
                  <p>{suggestedSummary}</p>
                  <EngineLanguageNote />
                </div>
              )}
              {suggestedGaps && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {t("screens.analyserPanel.gaps")}
                  </span>
                  <p>{suggestedGaps}</p>
                </div>
              )}
              {suggestedRecs && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {t("screens.analyserPanel.recommendations")}
                  </span>
                  <p>{suggestedRecs}</p>
                </div>
              )}
              {/* D1 -- the first click opens the comparison; it does
                  not write. What the Engine proposes replaces what a
                  person wrote, and she has to see both before it
                  does. */}
              {isDesignTest ? (
                <p
                  data-testid="analyser-design-test-note"
                  className="text-xs text-slate-600"
                >
                  {t("screens.analyserPanel.designTestNote")}{" "}
                  {drawerHref && (
                    <Link
                      to={drawerHref}
                      data-testid="analyser-design-test-open"
                      className="text-brand-brassDark hover:underline"
                    >
                      {t("screens.analyserPanel.designTestOpen")}
                    </Link>
                  )}
                </p>
              ) : !confirming ? (
                <button
                  type="button"
                  data-testid="analyser-accept-button"
                  onClick={() => setConfirming(true)}
                  className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy"
                >
                  {t("screens.analyserPanel.acceptSuggestion")}
                </button>
              ) : (
                <div
                  data-testid="analyser-accept-confirm"
                  className="space-y-2 rounded border border-amber-300 bg-amber-50 p-2"
                >
                  <p className="text-[11px] font-semibold text-amber-900">
                    {t("screens.analyserPanel.confirmHeading")}
                  </p>
                  {/* D2 -- the excerpt, printed BEFORE the accept.
                      A control graded Partial, ML 2, with a named gap
                      was lifted to compliant, ML 4 with no gaps by one
                      pasted sentence of the analyst's own prose, and
                      recorded in two clicks. The panel compared the
                      before and after honestly and never showed what
                      the after rested on. */}
                  <JudgementProvenance
                    excerpt={evidenceExcerpt}
                    anchored={evidenceAnchored}
                    fromDocuments={isDesignTest}
                    cappedReason={cappedReason}
                    where="confirm"
                  />
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-slate-500">
                        <th className="w-24 text-start font-medium" />
                        <th className="text-start font-medium">
                          {t("screens.analyserPanel.columnCurrent")}
                        </th>
                        <th className="text-start font-medium">
                          {t("screens.analyserPanel.columnSuggested")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="align-top">
                      {(
                        [
                          [
                            "judgement",
                            current ? stateLabel(current.status) : null,
                            stateLabel(suggestedStatus),
                          ],
                          [
                            "maturity",
                            current?.current_ml != null
                              ? String(current.current_ml)
                              : null,
                            suggestedMl != null ? String(suggestedMl) : null,
                          ],
                          [
                            "summary",
                            current?.evidence_summary ?? null,
                            suggestedSummary || null,
                          ],
                          ["gaps", current?.gaps ?? null, suggestedGaps || null],
                        ] as [string, string | null, string | null][]
                      ).map(([field, was, willBe]) => (
                        <tr key={field}>
                          <td className="py-0.5 pe-2 text-slate-500">
                            {t(`screens.analyserPanel.field.${field}`)}
                          </td>
                          <td className="py-0.5 pe-2 text-slate-800">
                            {was ?? (
                              <span className="text-slate-400">
                                {t("screens.analyserPanel.nothingRecorded")}
                              </span>
                            )}
                          </td>
                          <td className="py-0.5 text-slate-900">{willBe ?? "--"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="analyser-accept-confirm-button"
                      onClick={() => void handleAccept()}
                      disabled={upsert.isPending}
                      className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
                    >
                      {upsert.isPending
                        ? t("screens.analyserPanel.accepting")
                        : t("screens.analyserPanel.confirmAccept")}
                    </button>
                    <button
                      type="button"
                      data-testid="analyser-accept-cancel"
                      onClick={() => setConfirming(false)}
                      className="text-xs text-slate-600 hover:underline"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {run.status === "succeeded" && run.mode === "batch_cycle" && (
            <p data-testid="analyser-batch-summary" className="text-xs text-slate-700">
              {t("screens.analyserPanel.batchRunMaterialised")}{" "}
              <strong>{run.controls_graded ?? 0}</strong>{" "}
              {t("screens.analyserPanel.draftAssessment")}
              {run.controls_graded === 1 ? "" : "s"}{" "}
              {t("screens.analyserPanel.intoTheCycleReviewAndApproveIn")}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

// ── EngineStatusBadge (P31.3 / #553) ────────────────────────────────

/**
 * Tiny pill showing the wired Marsad Engine config + a "Test
 * connection" button. Renders inside the AnalyserPanel header so
 * an admin can confirm at a glance which provider + model the
 * analyser will actually hit before dispatching.
 *
 * States:
 *   - loading     -> slate "checking..." pill
 *   - configured  -> indigo pill "Engine: <provider> @ <model>" +
 *                    test button. After a click: emerald
 *                    "ok 1234 ms" or rose "failed: <error>".
 *   - !configured -> rose pill "Engine: not configured" + tooltip
 *                    pointing at the env-var docs.
 *   - forbidden   -> hidden (admin-only endpoint; the panel above
 *                    already 403-gates non-admins, but defensive).
 */
function EngineStatusBadge() {
  const { t } = useTranslation();
  const statusQuery = useQuery<EngineStatus>({
    queryKey: ["marsad-engine", "status"] as const,
    queryFn: () => api.get<EngineStatus>("/v1/admin/marsad-engine/status"),
    staleTime: 30_000,
  });
  const testMut = useMutation<EngineTestResult>({
    mutationFn: () => api.post<EngineTestResult>("/v1/admin/marsad-engine/test", {}),
  });

  if (statusQuery.isLoading) {
    return (
      <span
        data-testid="engine-status-badge"
        data-state="loading"
        className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
      >
        {t("screens.analyserPanel.engineChecking")}
      </span>
    );
  }
  if (statusQuery.isError || !statusQuery.data) {
    // Admin-only endpoint -- a 403 on a non-admin admin view is
    // possible (defensive). Hide instead of rendering noise.
    return null;
  }

  const s = statusQuery.data;
  if (!s.configured) {
    return (
      <span
        data-testid="engine-status-badge"
        data-state="not-configured"
        // The env-var instruction that used to sit here is for
        // somebody with shell access to the deployment. A preparer
        // has none, and the banner above already tells them what to
        // do instead.
        title={t("screens.analyserPanel.askAnAdministratorToConfigure")}
        className="inline-flex items-center gap-1 rounded bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-800 ring-1 ring-rose-200"
      >
        {t("screens.analyserPanel.engineNotConfigured")}
      </span>
    );
  }

  const testResult = testMut.data;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        data-testid="engine-status-badge"
        data-state="configured"
        data-provider={s.provider}
        data-model={s.model ?? ""}
        className="inline-flex items-center gap-1 rounded bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-900 ring-1 ring-indigo-200 tabular-nums"
      >
        {t("screens.analyserPanel.engine")} {s.provider} @ {s.model ?? "?"}
      </span>
      <button
        type="button"
        data-testid="engine-test-button"
        onClick={() => testMut.mutate()}
        disabled={testMut.isPending}
        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {testMut.isPending
          ? t("screens.analyserPanel.testing")
          : t("screens.analyserPanel.testConnection")}
      </button>
      {testResult && (
        <span
          data-testid="engine-test-result"
          data-ok={testResult.ok}
          className={
            "rounded px-2 py-0.5 text-[10px] font-medium tabular-nums " +
            (testResult.ok
              ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200"
              : "bg-rose-50 text-rose-900 ring-1 ring-rose-200")
          }
        >
          {testResult.ok
            ? `ok ${testResult.latency_ms} ms`
            : `failed: ${(testResult.error ?? "unknown").slice(0, 60)}`}
        </span>
      )}
    </div>
  );
}

// P61.2 (#566): Retry button rendered on failed analyser runs.
// Click fires useRetryAnalyserRun; backend gates on backoff via
// 429 + Retry-After header. The countdown timer reads the header
// off the thrown ApiError and disables the button until it elapses.
function RetryButton({ run, cycleId }: { run: AnalyserRun; cycleId: string }) {
  const { t } = useTranslation();
  const retry = useRetryAnalyserRun(cycleId);
  const toast = useToast();
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (cooldownEndsAt == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [cooldownEndsAt]);

  // Read the original control_id from the failed run's output_payload
  // if the LLM wrote one before failing. evidence_text isn't persisted
  // on the run row today; for v0.63.0 we keep it as empty string + let
  // the backend either accept that (treating as "use what's attached
  // now") or reject. UI follow-on: fetch the assessment's current
  // evidence chain at retry click time.
  const out = (run.output_payload ?? {}) as Record<string, unknown>;
  const controlId = (out["control_id"] as string | undefined) ?? null;

  const remainingSeconds =
    cooldownEndsAt == null ? 0 : Math.max(0, Math.ceil((cooldownEndsAt - now) / 1000));
  const onCooldown = remainingSeconds > 0;

  const onRetry = () => {
    if (!controlId) {
      toast.push({
        tone: "error",
        message: "Cannot retry: original control_id not found on the run",
        detail: "Use the dispatch dropdown to start a fresh run.",
      });
      return;
    }
    retry.mutate(
      { runId: run.id, control_id: controlId, evidence_text: "" },
      {
        onSuccess: () => {
          toast.push({
            tone: "success",
            message: `Retry queued (attempt ${run.retry_attempt + 1})`,
          });
          setCooldownEndsAt(null);
        },
        onError: (err) => {
          const apiErr = err as ApiError;
          if (apiErr.status === 429 && apiErr.retryAfterSeconds != null) {
            setCooldownEndsAt(Date.now() + apiErr.retryAfterSeconds * 1000);
            toast.push({
              tone: "warning",
              message: "Backoff active",
              detail: apiErr.detail,
            });
          } else {
            toast.push({
              tone: "error",
              message: "Retry failed",
              detail: apiErr.detail || apiErr.message,
            });
          }
        },
      },
    );
  };

  return (
    <button
      type="button"
      data-testid="analyser-run-retry"
      data-retry-cooldown-seconds={remainingSeconds}
      onClick={onRetry}
      disabled={onCooldown || retry.isPending}
      className="rounded border border-amber-400 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-900 disabled:opacity-50"
    >
      {retry.isPending
        ? t("screens.analyserPanel.queueing")
        : onCooldown
          ? `Retry in ${remainingSeconds}s`
          : `Retry (attempt ${run.retry_attempt + 1})`}
    </button>
  );
}
